import { autoUpdate } from '@floating-ui/dom';
import {
  Box,
  MenuProps,
  type PopoverActions,
  Skeleton,
  Stack,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { useForkRef } from '@mui/material/utils';
import { isUndefined } from 'lodash';
import * as React from 'react';
import { ComponentProps, useEffect, useMemo, useRef } from 'react';

import {
  SELECT_CHEVRON_ICON_SX,
  SelectChevronIcon,
} from '@/components/form/baseInputs/SelectChevronIcon';
import { MenuItemSecondaryTypography } from '@/components/typography/MenuItemSecondaryTypography';
import { MenuItemTypography } from '@/components/typography/MenuItemTypography';
import { EMPTY_CONTENT_HYPHEN } from '@/components/typography/placeholders';
import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseSelectInputProps,
  FormControlSelectInputProps,
  HelpTextVariant,
} from '../inputTypes';
const SELECT_DISPLAY_STYLE_XS: React.CSSProperties = {
  minHeight: 'unset',
  paddingTop: '6px',
  paddingBottom: '6px',
  paddingLeft: '8px',
};

export interface SelectInputProps<V = unknown>
  extends FormControlSelectInputProps<V> {
  label: string;
  id?: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  hideLabel?: boolean;
  // Optional boolean that controls the open state of the select input
  // https://mui.com/material-ui/api/select/#Select-prop-open
  open?: ComponentProps<typeof Select>['open'];
  labelIconEnd?: React.ReactNode;
  emptyValueDisplay?: string;
  showEmptyValue?: boolean;
  MenuProps?: Partial<MenuProps>;
}

// HTMLAttributes<HTMLDivElement> doesn't include `data-*` attributes
interface SelectDisplayPropsType extends React.HTMLAttributes<HTMLDivElement> {
  'data-testid'?: string;
}

const BaseSelectInput = ({
  options,
  testId,
  required,
  emptyValueDisplay,
  placeholder,
  showEmptyValue = true,
  labelId,
  loading,
  size = 'sm',
  MenuProps: customMenuProps,
  ...inputProps
}: BaseSelectInputProps<string> & { MenuProps?: Partial<MenuProps> }) => {
  const { disabled, startAdornment, endAdornment, sx, ...inputPropsRest } =
    inputProps;
  const selectRef = useRef<HTMLDivElement>(null);
  const menuActions = useRef<PopoverActions>(null);
  const actionRef = useForkRef(menuActions, customMenuProps?.action);
  const stopAutoUpdate = useRef<(() => void) | undefined>(undefined);
  useEffect(() => () => stopAutoUpdate.current?.(), []);
  const disabledStyle = disabled ? { background: COLORS.GRAY[50] } : {};
  // Hide the selected option's icon if the parent Select has a startAdornment
  const selectedOptionIconStyle = startAdornment
    ? {
        '& .MuiListItemIcon-root': {
          display: 'none',
        },
      }
    : {};
  // Hide the selected option's icon if it is the current value in the Select
  const selectedOptionEndIconStyle = {
    '& .MuiSelect-select .MuiStack-root .end-adornment': {
      display: 'none',
    },
  };
  const mergedSx = [
    { background: 'white' },
    disabledStyle,
    selectedOptionIconStyle,
    selectedOptionEndIconStyle,
    SELECT_CHEVRON_ICON_SX,
    ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
  ];

  const selectDisplayStyle =
    size === 'xs' ? SELECT_DISPLAY_STYLE_XS : undefined;

  const disallowEmptySelection = required;

  const emptyValueOption = useMemo(
    () =>
      showEmptyValue && (
        <MenuItem
          value=""
          disabled={disallowEmptySelection}
          sx={(theme) => ({
            height: theme.spacing(4.5),
          })}
        >
          {emptyValueDisplay || EMPTY_CONTENT_HYPHEN}
        </MenuItem>
      ),
    [disallowEmptySelection, emptyValueDisplay, showEmptyValue]
  );

  // HACK: we wrap the endAdornment in a span to ensure that
  // we leave space for the dropdown chevron
  const wrappedEndAdornment = endAdornment ? (
    <Box
      component="span"
      sx={{
        position: 'absolute',
        right: '28px', // Leave space for the dropdown chevron
        top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none',
      }}
    >
      {endAdornment}
    </Box>
  ) : null;

  const renderValue = useMemo(() => {
    const hasSecondaryText = options.some(
      (opt) => opt.type !== 'component' && opt.secondaryText
    );

    if (!hasSecondaryText && !placeholder) {
      return undefined;
    }

    return function renderSelectedValue(selectedValue: string) {
      if (isUndefined(selectedValue) || selectedValue === '') {
        if (placeholder) {
          return (
            <Box component="span" sx={{ color: COLORS.GRAY[400] }}>
              {placeholder}
            </Box>
          );
        }
        return emptyValueDisplay || EMPTY_CONTENT_HYPHEN;
      }

      const selectedOption = options.find(
        (opt) => opt.type !== 'component' && opt.value === selectedValue
      );

      if (!selectedOption || selectedOption.type === 'component') {
        return selectedValue;
      }

      return selectedOption.display;
    };
  }, [options, emptyValueDisplay, placeholder]);

  return (
    // this select is a non-optimal experience for mobile; consider using NativeSelect here for a mobile device
    <Select
      ref={selectRef}
      disabled={disabled}
      displayEmpty
      IconComponent={SelectChevronIcon}
      sx={mergedSx}
      inputProps={{
        sx: mergedSx,
      }}
      {...inputPropsRest}
      required={required}
      startAdornment={startAdornment}
      endAdornment={wrappedEndAdornment}
      renderValue={renderValue}
      MenuProps={{
        anchorOrigin: { horizontal: 'left', vertical: 'bottom' },
        transformOrigin: { horizontal: 'left', vertical: 'top' },
        PaperProps: { sx: { maxHeight: 400 } },
        ...customMenuProps,
        action: actionRef,
        TransitionProps: {
          ...customMenuProps?.TransitionProps,
          onEntering: (element, isAppearing) => {
            stopAutoUpdate.current?.();
            if (selectRef.current) {
              stopAutoUpdate.current = autoUpdate(
                selectRef.current,
                element,
                () => menuActions.current?.updatePosition()
              );
            }
            customMenuProps?.TransitionProps?.onEntering?.(
              element,
              isAppearing
            );
          },
          onExit: (element) => {
            stopAutoUpdate.current?.();
            customMenuProps?.TransitionProps?.onExit?.(element);
          },
        },
        ...(customMenuProps?.PaperProps && {
          PaperProps: {
            sx: { maxHeight: 400 },
            ...customMenuProps.PaperProps,
          },
        }),
      }}
      SelectDisplayProps={
        {
          'data-testid': testId ?? `select-${inputProps.name ?? ''}`,
          ...(selectDisplayStyle && { style: selectDisplayStyle }),
        } as SelectDisplayPropsType
      }
      labelId={labelId}
    >
      {emptyValueOption}
      {options.map((option, i) => {
        // don't wrap the component if it has a value -- that needs to be persisted so the
        // item can be selected
        if (option.type === 'component' && !isUndefined(option.value)) {
          return option.component;
        } else if (option.type === 'component') {
          const Component = () => option.component;
          return <Component key={i} />;
        } else {
          return (
            <MenuItem
              disabled={option.disabled}
              divider={option.withDividerBelow}
              key={i}
              value={option.value}
              sx={(theme) => ({
                height: option.secondaryText ? 'auto' : theme.spacing(4.5),
                py: option.secondaryText ? theme.spacing(1.25) : undefined,
              })}
              data-testid={`select-option-${option.value}`}
            >
              {option.startAdornment ||
              option.endAdornment ||
              option.secondaryText ? (
                // When displaying the option with icon and text as the selected option,
                // they need to be wrapped in a Stack to ensure horizontal alignment.
                // Within that, the text overflow needs to be handled with ellipses.
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent={
                    option.endAdornment ? 'space-between' : undefined
                  }
                  width={option.endAdornment ? '100%' : undefined}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-start"
                  >
                    {option.startAdornment}
                    <Stack direction="column" spacing={0}>
                      <MenuItemTypography
                        textOverflow="ellipsis"
                        overflow="hidden"
                        whiteSpace="nowrap"
                      >
                        {option.display}
                      </MenuItemTypography>
                      {option.secondaryText && (
                        <MenuItemSecondaryTypography
                          textOverflow="ellipsis"
                          overflow="hidden"
                          whiteSpace="nowrap"
                        >
                          {option.secondaryText}
                        </MenuItemSecondaryTypography>
                      )}
                    </Stack>
                  </Stack>
                  {option.endAdornment}
                </Stack>
              ) : (
                // When just displaying the text, the MUI Select Input ellipsizes
                // the overflow text of the selected option.
                option.display
              )}
            </MenuItem>
          );
        }
      })}
      {loading && (
        <>
          {Array.from({ length: 3 }).map((_, idx) => (
            <MenuItem key={idx} sx={{ py: 0 }}>
              <Skeleton height={40} width="100%" />
            </MenuItem>
          ))}
        </>
      )}
    </Select>
  );
};

export function SelectInput<V = unknown>({
  id,
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  labelIconEnd,
  ...inputProps
}: SelectInputProps<V>) {
  return (
    <FormControl<BaseSelectInputProps<V>>
      hideLabel={hideLabel}
      id={id}
      contextualHelp={contextualHelp}
      component={BaseSelectInput}
      componentKind="select"
      label={label}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      required={inputProps.required}
      errorMessage={errorMessage}
      inputProps={inputProps}
      labelIconEnd={labelIconEnd}
    />
  );
}
